import { useState, useEffect, useCallback, useRef } from "react";

import { API } from "../config";

// v127: dropdown Mercato con 11 mercati gestiti dalla pipeline + "Altro".
// I codici (value) corrispondono a quelli usati dalla funzione _esito
// in pipeline_risultati.py (case-sensitive). Le label sono le diciture
// mostrate all'utente nel dropdown.
const MERCATI_OPZIONI = [
  { value: "1",       label: "1X2 — 1" },
  { value: "X",       label: "1X2 — X" },
  { value: "2",       label: "1X2 — 2" },
  { value: "U15",     label: "Under 1.5 gol" },
  { value: "O15",     label: "Over 1.5 gol" },
  { value: "U25",     label: "Under 2.5 gol" },
  { value: "O25",     label: "Over 2.5 gol" },
  { value: "U35",     label: "Under 3.5 gol" },
  { value: "O35",     label: "Over 3.5 gol" },
  { value: "BTTS_SI", label: "Goal (entrambe segnano)" },
  { value: "BTTS_NO", label: "NoGoal (almeno una non segna)" },
  { value: "ALTRO",   label: "Altro (esito da inserire manualmente)" },
];

// ── Stili coerenti con DiarioBookmaker.js / DiarioCapitale.js ──
const FONT = "'DM Sans', sans-serif";

const stili = {
  modalOverlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(15, 23, 42, 0.5)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    background: "#fff", borderRadius: 12,
    padding: "24px 28px", width: 560, maxWidth: "92vw",
    maxHeight: "92vh", overflowY: "auto",
    fontFamily: FONT,
    boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
  },
  modalTitolo: {
    fontSize: 18, fontWeight: 700, color: "#1E293B",
    margin: "0 0 6px 0",
  },
  modalSottotitolo: {
    fontSize: 12, color: "#64748B", marginBottom: 18,
  },
  sezione: {
    marginBottom: 18,
    paddingBottom: 4,
  },
  sezioneTitolo: {
    fontSize: 11, fontWeight: 700, color: "#64748B",
    textTransform: "uppercase", letterSpacing: 0.5,
    marginBottom: 12, paddingBottom: 6,
    borderBottom: "1px solid #E2E8F0",
  },
  riga2col: {
    display: "grid", gridTemplateColumns: "1fr 1fr",
    gap: 12, marginBottom: 12,
  },
  campoRiga: {
    marginBottom: 12,
  },
  label: {
    fontSize: 12, fontWeight: 600, color: "#475569",
    marginBottom: 4, display: "block",
  },
  labelObbl: {
    color: "#DC2626", marginLeft: 2,
  },
  input: {
    fontFamily: FONT, fontSize: 13, padding: "8px 10px",
    borderRadius: 6, border: "1px solid #CBD5E1",
    background: "#fff", color: "#1E293B", width: "100%",
    boxSizing: "border-box", outline: "none",
  },
  inputDisabilitato: {
    fontFamily: FONT, fontSize: 13, padding: "8px 10px",
    borderRadius: 6, border: "1px solid #E2E8F0",
    background: "#F8FAFC", color: "#94A3B8", width: "100%",
    boxSizing: "border-box", outline: "none",
    cursor: "not-allowed",
  },
  textarea: {
    fontFamily: FONT, fontSize: 13, padding: "8px 10px",
    borderRadius: 6, border: "1px solid #CBD5E1",
    background: "#fff", color: "#1E293B", width: "100%",
    boxSizing: "border-box", outline: "none",
    resize: "vertical", minHeight: 50,
  },
  select: {
    fontFamily: FONT, fontSize: 13, padding: "8px 10px",
    borderRadius: 6, border: "1px solid #CBD5E1",
    background: "#fff", color: "#1E293B", width: "100%",
    boxSizing: "border-box", outline: "none",
  },
  radioGroup: {
    display: "flex", gap: 16, alignItems: "center",
  },
  radioLabel: {
    display: "flex", alignItems: "center", gap: 6,
    fontSize: 13, color: "#1E293B", cursor: "pointer",
  },
  modalAzioni: {
    display: "flex", justifyContent: "flex-end",
    gap: 10, marginTop: 20,
  },
  btn: {
    fontFamily: FONT, fontSize: 13, fontWeight: 600,
    padding: "8px 18px", borderRadius: 6, border: "none",
    cursor: "pointer", transition: "background 0.15s",
  },
  btnPrimary: {
    background: "#2563EB", color: "#fff",
  },
  btnSecondary: {
    background: "#F1F5F9", color: "#475569",
    border: "1px solid #CBD5E1",
  },
  errore: {
    background: "#FFEBEE", color: "#C62828",
    padding: "10px 12px", borderRadius: 6,
    fontSize: 12, marginBottom: 14,
  },
  notaInfo: {
    fontSize: 11, color: "#94A3B8", fontStyle: "italic",
    marginTop: 4,
  },
};

// ── Utility ─────────────────────────────────────────────────────
function oggi() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// v125: helper formattazione € con separatore migliaia "." e decimali ","
// (regex manuale, no toLocaleString — bug noto V8 sui numeri 1000-9999).
function formatEur(n) {
  if (n === null || n === undefined) return "—";
  const num = Number(n);
  if (!isFinite(num)) return "—";
  const negativo = num < 0;
  const abs = Math.abs(num);
  const intero = Math.trunc(abs);
  const dec = Math.round((abs - intero) * 100);
  const decStr = String(dec).padStart(2, "0");
  const interoStr = String(intero).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return (negativo ? "-" : "") + interoStr + "," + decStr + " €";
}

// ── Componente principale ───────────────────────────────────────
export default function FormOperazione({ operazioneEsistente, precompila, onChiudi, onSalva }) {
  const inModifica = !!operazioneEsistente;

  // v128: alias di inizializzazione. Se è un'op in modifica (operazioneEsistente)
  // legge da lì; altrimenti, se arriva un oggetto precompila dal Monitor Live,
  // legge da quello. Il flag inModifica resta legato SOLO a operazioneEsistente,
  // così il salvataggio resta POST (nuova) anche con precompila valorizzato.
  const initData = operazioneEsistente || precompila || null;

  // Stato form
  const [dataPartita, setDataPartita]   = useState(initData?.data_partita || oggi());
  const [oraPartita, setOraPartita]     = useState(initData?.ora_partita?.slice(0, 5) || "");
  const [casa, setCasa]                 = useState(initData?.casa || "");
  const [trasferta, setTrasferta]       = useState(initData?.trasferta || "");
  const [competizione, setCompetizione] = useState(initData?.competizione || "");
  const [sistemaId, setSistemaId]       = useState(initData?.sistema_id || "");
  const [mercato, setMercato]           = useState(initData?.mercato || "");
  const [tipoPB, setTipoPB]             = useState(initData?.tipo_punta_banca || "Punta");
  const [quotaTeorica, setQuotaTeorica] = useState(initData?.quota_teorica ?? "");
  const [quotaEffettiva, setQuotaEffettiva] = useState(operazioneEsistente?.quota_effettiva ?? "");
  const [bookmakerId, setBookmakerId]   = useState(operazioneEsistente?.bookmaker_id ?? "");
  const [feePct, setFeePct]             = useState(operazioneEsistente?.fee_pct ?? "");

  // ── Stake e Size% mutuamente esclusivi ──
  const valSizePct = operazioneEsistente?.size_pct;
  const valStake   = operazioneEsistente?.size_eur_snapshot;
  const startMode  = (valSizePct && Number(valSizePct) > 0) ? "size"
                   : (valStake   && Number(valStake) > 0)   ? "stake"
                   : "size";
  const [modalitaSize, setModalitaSize] = useState(startMode);
  const [sizePct, setSizePct] = useState(
    startMode === "size" && valSizePct ? valSizePct : ""
  );
  const [stake, setStake] = useState(
    startMode === "stake" && valStake ? valStake : ""
  );

  const [esito, setEsito] = useState(operazioneEsistente?.esito_operazione || "Open");
  const [note, setNote]   = useState(operazioneEsistente?.note || "");

  // v127: suggerimenti datalist popolati dalle partite della data scelta.
  // Si aggiornano quando l'utente cambia data_partita.
  const [suggSquadre, setSuggSquadre]           = useState([]);
  const [suggCompetizioni, setSuggCompetizioni] = useState([]);

  const [bookmakers, setBookmakers] = useState([]);
  const [sistemi, setSistemi]       = useState([]);
  // Mappa sistema_id → size_pct_default letta da diario_size_per_sistema.
  // Usata per precompilare Size% quando l'utente seleziona un sistema dal dropdown.
  const [sizePerSistema, setSizePerSistema] = useState({});

  // Lista revisioni capitale di riferimento (ordinata DESC per da_data).
  // Usata per calcolare il capitale attivo alla data della partita.
  const [tagliandiCapitale, setTagliandiCapitale] = useState([]);

  const [errore, setErrore] = useState(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    fetch(`${API}/diario/bookmaker`)
      .then(r => r.ok ? r.json() : Promise.reject("HTTP " + r.status))
      .then(data => {
        const attivi = data.filter(b => b.stato === "attivo");
        setBookmakers(attivi);
        if (!inModifica && !operazioneEsistente?.bookmaker_id) {
          const def = attivi.find(b => b.is_default);
          if (def) {
            setBookmakerId(def.id);
            setFeePct(prev => prev !== "" ? prev : (def.fee_pct_default ?? ""));
          }
        }
      })
      .catch(e => console.error("Errore caricamento bookmaker:", e));
  }, [inModifica, operazioneEsistente]);

  useEffect(() => {
    fetch(`${API}/sistemi`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (Array.isArray(data)) setSistemi(data);
      })
      .catch(() => {});
  }, []);

  // Carica la mappa Size% per sistema (tab ⚙️ Impostazioni → Size per sistema).
  // Se un sistema non è presente nella mappa significa che non è stato aggiunto
  // al Diario: in quel caso, alla selezione dal dropdown, il Size% resta vuoto.
  useEffect(() => {
    fetch(`${API}/diario/size-per-sistema`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (Array.isArray(data)) {
          const mappa = {};
          data.forEach(r => {
            if (r.sistema_id !== null && r.sistema_id !== undefined) {
              mappa[r.sistema_id] = r.size_pct_default;
            }
          });
          setSizePerSistema(mappa);
        }
      })
      .catch(() => {});
  }, []);

  // Carica la lista delle revisioni del capitale di riferimento.
  // Serve per calcolare il capitale attivo alla data della partita
  // (logica identica a _diario_capitale_attivo_a_data nel backend).
  useEffect(() => {
    fetch(`${API}/diario/capitale/tagliandi`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (Array.isArray(data)) setTagliandiCapitale(data);
      })
      .catch(() => {});
  }, []);

  // v127: carica le partite della data scelta e popola i datalist
  // di suggerimento per Casa, Trasferta e Competizione.
  // Riusa l'endpoint /partite/cerca esistente (filtrato per data).
  useEffect(() => {
    if (!dataPartita) {
      setSuggSquadre([]);
      setSuggCompetizioni([]);
      return;
    }
    fetch(`${API}/partite/cerca?data=${encodeURIComponent(dataPartita)}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (!Array.isArray(data)) return;
        const squadre = new Set();
        const leghe = new Set();
        data.forEach(p => {
          if (p.casa) squadre.add(p.casa);
          if (p.trasferta) squadre.add(p.trasferta);
          if (p.lega) leghe.add(p.lega);
        });
        setSuggSquadre(Array.from(squadre).sort());
        setSuggCompetizioni(Array.from(leghe).sort());
      })
      .catch(() => {});
  }, [dataPartita]);

  const cambiaBookmaker = useCallback((idStr) => {
    const id = idStr === "" ? "" : Number(idStr);
    setBookmakerId(id);
    if (id !== "") {
      const b = bookmakers.find(x => x.id === id);
      if (b && (feePct === "" || feePct === null)) {
        setFeePct(b.fee_pct_default ?? "");
      }
    }
  }, [bookmakers, feePct]);

  // Restituisce il capitale di riferimento attivo a una certa data (formato YYYY-MM-DD).
  // Logica identica a _diario_capitale_attivo_a_data nel backend:
  // prende la prima revisione (ordinata DESC per da_data) con da_data <= data.
  // Restituisce 0 se nessuna revisione è valida (es. nessuna inserita o tutte future).
  const capitaleAllaData = useCallback((dataIso) => {
    if (!dataIso || !Array.isArray(tagliandiCapitale) || tagliandiCapitale.length === 0) {
      return 0;
    }
    const valida = tagliandiCapitale.find(t => t.da_data && t.da_data <= dataIso);
    if (!valida) return 0;
    const v = Number(valida.capitale_riferimento_eur);
    return Number.isFinite(v) ? v : 0;
  }, [tagliandiCapitale]);

  // Capitale di riferimento attivo alla data della partita.
  // Si aggiorna automaticamente se l'utente cambia la data nel form.
  const capitaleRif = capitaleAllaData(dataPartita);

  // Indica se l'operazione è "fuori sistema" (nessun sistema selezionato).
  // In questo caso Size% è disabilitato e l'utente inserisce solo lo Stake.
  const fuoriSistema = !sistemaId;

  // Sincronizzazione bidirezionale Size% ↔ Stake (Decisione 43 v121).
  // - Se l'utente modifica Size%, lo Stake si ricalcola: Stake = capitale × Size% / 100
  // - Se l'utente modifica Stake, il Size% si ricalcola: Size% = Stake / capitale × 100
  // - Se capitale = 0, la sincronizzazione non altera l'altro campo (lascio com'è).
  const cambiaSize = (v) => {
    setSizePct(v);
    setModalitaSize("size");
    if (v === "" || v === null) {
      setStake("");
      return;
    }
    const sp = Number(v);
    if (capitaleRif > 0 && Number.isFinite(sp)) {
      const nuovoStake = (capitaleRif * sp) / 100;
      setStake(nuovoStake.toFixed(2));
    }
  };
  const cambiaStake = (v) => {
    setStake(v);
    setModalitaSize("stake");
    if (v === "" || v === null) {
      // Se l'utente svuota lo Stake e siamo dentro a un sistema, non azzero il Size%
      // (potrebbe stare ancora ricalcolando). Azzero solo se anche Size% è vuoto.
      return;
    }
    const st = Number(v);
    if (capitaleRif > 0 && Number.isFinite(st)) {
      const nuovaSize = (st / capitaleRif) * 100;
      setSizePct(nuovaSize.toFixed(4));
    }
  };

  // Cambio sistema dal dropdown.
  // Se il sistema selezionato è presente in diario_size_per_sistema, precompila
  // il campo Size% con il valore di default configurato (e ricalcola lo Stake
  // di conseguenza, grazie a cambiaSize aggiornato con la sincronizzazione).
  // Se non è nel Diario o se l'utente sceglie "— Nessuno —", lascia Size% com'è.
  const cambiaSistema = (idStr) => {
    setSistemaId(idStr);
    if (idStr === "") return; // "— Nessuno —" → non tocco Size%
    if (Object.prototype.hasOwnProperty.call(sizePerSistema, idStr)) {
      const def = sizePerSistema[idStr];
      if (def !== null && def !== undefined && def !== "") {
        cambiaSize(String(def));
      }
    }
    // Se il sistema NON è nel Diario, lascio Size% com'è.
  };

  // v128 fix: pre-compilazione automatica Size% quando il form si apre con
  // precompila (apertura da Monitor Live → "📓 Invia al Diario").
  // Scatta una sola volta, quando:
  //   - siamo in flusso precompila (NON modifica)
  //   - il sistema è già impostato dalla prop precompila
  //   - la mappa sizePerSistema è stata caricata dall'API
  //   - l'utente non ha ancora toccato Size% (resta a "")
  // Replica esattamente la logica di cambiaSistema, applicata al sistema iniziale.
  const sizePrefillFatto = useRef(false);
  useEffect(() => {
    if (sizePrefillFatto.current) return;
    if (inModifica) return;
    if (!precompila) return;
    if (!sistemaId) return;
    if (sizePct !== "" && sizePct !== null) return;
    if (!Object.prototype.hasOwnProperty.call(sizePerSistema, sistemaId)) return;
    const def = sizePerSistema[sistemaId];
    if (def === null || def === undefined || def === "") return;
    cambiaSize(String(def));
    sizePrefillFatto.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sizePerSistema, sistemaId, precompila, inModifica]);

  const salva = async () => {
    setErrore(null);

    if (!dataPartita) { setErrore("Data partita obbligatoria"); return; }
    if (!casa.trim()) { setErrore("Squadra di casa obbligatoria"); return; }
    if (!trasferta.trim()) { setErrore("Squadra in trasferta obbligatoria"); return; }
    if (!mercato.trim()) { setErrore("Mercato obbligatorio"); return; }
    if (!tipoPB) { setErrore("Tipo (Punta/Banca) obbligatorio"); return; }
    const qe = Number(quotaEffettiva);
    if (!qe || qe <= 1) { setErrore("Quota effettiva deve essere maggiore di 1"); return; }
    if (!bookmakerId) { setErrore("Bookmaker obbligatorio"); return; }

    const payload = {
      data_partita: dataPartita,
      casa: casa.trim(),
      trasferta: trasferta.trim(),
      mercato: mercato.trim(),
      tipo_punta_banca: tipoPB,
      quota_effettiva: qe,
      bookmaker_id: Number(bookmakerId),
      esito_operazione: esito,
    };

    if (oraPartita) payload.ora_partita = oraPartita;
    if (competizione.trim()) payload.competizione = competizione.trim();
    if (sistemaId) payload.sistema_id = sistemaId;
    if (quotaTeorica !== "" && quotaTeorica !== null) payload.quota_teorica = Number(quotaTeorica);
    if (feePct !== "" && feePct !== null) payload.fee_pct = Number(feePct);

    // --- Logica salvataggio Size%/Stake (Decisione 43 v121 + v124) ---
    // - Sistema selezionato → mando size_pct, il backend calcola lo stake da capitale × size_pct / 100
    //   (così è sempre coerente con il capitale di riferimento alla data partita).
    // - Nessun sistema (fuori sistema) → mando solo size_eur_manuale (stake), il backend mette size_pct=0.
    if (sistemaId) {
      // Operazione legata a un sistema → uso Size%
      if (sizePct !== "" && sizePct !== null) {
        payload.size_pct = Number(sizePct);
      }
    } else {
      // Operazione fuori sistema → uso Stake manuale
      if (stake !== "" && stake !== null) {
        payload.size_eur_manuale = Number(stake);
      }
    }

    if (note.trim()) payload.note = note;

    // v128: selezione_id propagato silenziosamente al POST quando si arriva
    // dal Monitor Live tramite la prop precompila. Permette il match
    // deterministico (selezione_id → partita) all'auto-aggiornamento esiti.
    if (!inModifica && precompila?.selezione_id) {
      payload.selezione_id = Number(precompila.selezione_id);
    }

    setSalvando(true);
    try {
      const url = inModifica
        ? `${API}/diario/operazioni/${operazioneEsistente.id}`
        : `${API}/diario/operazioni`;
      const method = inModifica ? "PUT" : "POST";
      const r = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || err.errore || `HTTP ${r.status}`);
      }
      const data = await r.json();
      if (data.errore) throw new Error(data.errore);
      onSalva(data);
    } catch (e) {
      setErrore(e.message || "Errore di rete");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div style={stili.modalOverlay} onClick={onChiudi}>
      <div style={stili.modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={stili.modalTitolo}>
          {inModifica ? "Modifica operazione" : "Nuova operazione manuale"}
        </h2>
        <div style={stili.modalSottotitolo}>
          {inModifica
            ? `ID #${operazioneEsistente.id} — modifiche all'operazione esistente`
            : "Inserimento manuale di un'operazione (non collegata al Monitor Live)"}
        </div>

        {errore && <div style={stili.errore}>{errore}</div>}

        {/* ── SEZIONE: Partita ───────────────────────── */}
        <div style={stili.sezione}>
          <div style={stili.sezioneTitolo}>Partita</div>

          <div style={stili.riga2col}>
            <div>
              <label style={stili.label}>
                Data partita <span style={stili.labelObbl}>*</span>
              </label>
              <input
                type="date"
                style={stili.input}
                value={dataPartita}
                onChange={(e) => setDataPartita(e.target.value)}
              />
            </div>
            <div>
              <label style={stili.label}>Ora partita</label>
              <input
                type="time"
                style={stili.input}
                value={oraPartita}
                onChange={(e) => setOraPartita(e.target.value)}
              />
            </div>
          </div>

          <div style={stili.riga2col}>
            <div>
              <label style={stili.label}>
                Squadra di casa <span style={stili.labelObbl}>*</span>
              </label>
              <input
                type="text"
                style={stili.input}
                value={casa}
                onChange={(e) => setCasa(e.target.value)}
                placeholder="Es. Milan"
                list="datalist-squadre"
                autoComplete="off"
              />
            </div>
            <div>
              <label style={stili.label}>
                Squadra in trasferta <span style={stili.labelObbl}>*</span>
              </label>
              <input
                type="text"
                style={stili.input}
                value={trasferta}
                onChange={(e) => setTrasferta(e.target.value)}
                placeholder="Es. Inter"
                list="datalist-squadre"
                autoComplete="off"
              />
            </div>
          </div>

          <div style={stili.campoRiga}>
            <label style={stili.label}>Competizione</label>
            <input
              type="text"
              style={stili.input}
              value={competizione}
              onChange={(e) => setCompetizione(e.target.value)}
              placeholder="Es. Serie A"
              list="datalist-competizioni"
              autoComplete="off"
            />
          </div>

          {/* v127: datalist condivisi popolati dalle partite della data scelta */}
          <datalist id="datalist-squadre">
            {suggSquadre.map(s => <option key={s} value={s} />)}
          </datalist>
          <datalist id="datalist-competizioni">
            {suggCompetizioni.map(c => <option key={c} value={c} />)}
          </datalist>
        </div>

        {/* ── SEZIONE: Mercato e quote ─────────────── */}
        <div style={stili.sezione}>
          <div style={stili.sezioneTitolo}>Mercato e quote</div>

          <div style={stili.riga2col}>
            <div>
              <label style={stili.label}>
                Mercato <span style={stili.labelObbl}>*</span>
              </label>
              <select
                style={stili.select}
                value={mercato}
                onChange={(e) => setMercato(e.target.value)}
              >
                <option value="">— Seleziona —</option>
                {MERCATI_OPZIONI.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              {mercato === "ALTRO" && (
                <div style={{
                  fontSize: 11,
                  color: "#92400E",
                  background: "#FEF3C7",
                  border: "1px solid #FDE68A",
                  borderRadius: 6,
                  padding: "6px 8px",
                  marginTop: 6,
                  lineHeight: 1.4,
                }}>
                  ⚠️ Per operazioni con mercato "Altro" l'esito non verrà aggiornato automaticamente: dovrai inserirlo manualmente. Specifica il mercato nel campo Note.
                </div>
              )}
            </div>
            <div>
              <label style={stili.label}>Sistema (opzionale)</label>
              {sistemi.length > 0 ? (
                <select
                  style={stili.select}
                  value={sistemaId}
                  onChange={(e) => cambiaSistema(e.target.value)}
                >
                  <option value="">— Nessuno —</option>
                  {sistemi.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.nome || s.id}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  style={stili.input}
                  value={sistemaId}
                  onChange={(e) => cambiaSistema(e.target.value)}
                  placeholder="ID sistema"
                />
              )}
            </div>
          </div>

          <div style={stili.campoRiga}>
            <label style={stili.label}>
              Tipo <span style={stili.labelObbl}>*</span>
            </label>
            <div style={stili.radioGroup}>
              <label style={stili.radioLabel}>
                <input
                  type="radio"
                  name="tipoPB"
                  value="Punta"
                  checked={tipoPB === "Punta"}
                  onChange={() => setTipoPB("Punta")}
                />
                Punta
              </label>
              <label style={stili.radioLabel}>
                <input
                  type="radio"
                  name="tipoPB"
                  value="Banca"
                  checked={tipoPB === "Banca"}
                  onChange={() => setTipoPB("Banca")}
                />
                Banca
              </label>
            </div>
          </div>

          <div style={stili.riga2col}>
            <div>
              <label style={stili.label}>Quota teorica</label>
              <input
                type="number"
                step="0.01"
                style={stili.input}
                value={quotaTeorica}
                onChange={(e) => setQuotaTeorica(e.target.value)}
                placeholder="Es. 2.10"
              />
            </div>
            <div>
              <label style={stili.label}>
                Quota effettiva <span style={stili.labelObbl}>*</span>
              </label>
              <input
                type="number"
                step="0.01"
                style={stili.input}
                value={quotaEffettiva}
                onChange={(e) => setQuotaEffettiva(e.target.value)}
                placeholder="Es. 2.05"
              />
            </div>
          </div>
        </div>

        {/* ── SEZIONE: Bookmaker e dimensione ──────── */}
        <div style={stili.sezione}>
          <div style={stili.sezioneTitolo}>Bookmaker e dimensione</div>

          <div style={stili.campoRiga}>
            <label style={stili.label}>
              Bookmaker <span style={stili.labelObbl}>*</span>
            </label>
            <select
              style={stili.select}
              value={bookmakerId}
              onChange={(e) => cambiaBookmaker(e.target.value)}
            >
              <option value="">— Seleziona —</option>
              {bookmakers.map(b => (
                <option key={b.id} value={b.id}>
                  {b.nome}{b.is_default ? " (predefinito)" : ""}
                </option>
              ))}
            </select>
          </div>

          <div style={stili.campoRiga}>
            <label style={stili.label}>Fee %</label>
            <input
              type="number"
              step="0.01"
              style={stili.input}
              value={feePct}
              onChange={(e) => setFeePct(e.target.value)}
              placeholder="Es. 5"
            />
          </div>

          <div style={stili.riga2col}>
            <div>
              <label style={stili.label}>Size %</label>
              <input
                type="number"
                step="0.01"
                style={fuoriSistema ? stili.inputDisabilitato : stili.input}
                value={sizePct}
                onChange={(e) => cambiaSize(e.target.value)}
                placeholder={fuoriSistema ? "—" : "Es. 2"}
                disabled={fuoriSistema}
              />
            </div>
            <div>
              <label style={stili.label}>Stake (€)</label>
              <input
                type="number"
                step="0.01"
                style={stili.input}
                value={stake}
                onChange={(e) => cambiaStake(e.target.value)}
                placeholder="Es. 100"
              />
            </div>
          </div>
          <div style={stili.notaInfo}>
            {fuoriSistema ? (
              <>Operazione <strong>fuori sistema</strong>: inserisci direttamente lo Stake in €. Il Size % non si applica.</>
            ) : (
              <>
                Size % e Stake si aggiornano automaticamente l'uno in base all'altro. Capitale di riferimento alla data {dataPartita ? dataPartita.split("-").reverse().join("/") : "—"}: <strong>{capitaleRif > 0 ? formatEur(capitaleRif) : "non configurato"}</strong>.
                {capitaleRif === 0 && (
                  <> Inserisci prima un valore per il capitale di riferimento nella tab Impostazioni.</>
                )}
              </>
            )}
          </div>
        </div>

        {/* ── SEZIONE: Esito ─────────────────────── */}
        <div style={stili.sezione}>
          <div style={stili.sezioneTitolo}>Esito</div>
          <div style={stili.campoRiga}>
            <label style={stili.label}>Esito operazione</label>
            <select
              style={stili.select}
              value={esito}
              onChange={(e) => setEsito(e.target.value)}
            >
              <option value="Open">Open (in attesa)</option>
              <option value="Vinta">Vinta</option>
              <option value="Persa">Persa</option>
            </select>
          </div>
        </div>

        {/* ── SEZIONE: Note ─────────────────── */}
        <div style={stili.sezione}>
          <div style={stili.sezioneTitolo}>Note</div>
          <textarea
            style={stili.textarea}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note libere (opzionale)"
          />
        </div>

        {/* ── Azioni ─────────────────────────────── */}
        <div style={stili.modalAzioni}>
          <button
            style={{ ...stili.btn, ...stili.btnSecondary }}
            onClick={onChiudi}
            disabled={salvando}
          >
            Annulla
          </button>
          <button
            style={{ ...stili.btn, ...stili.btnPrimary }}
            onClick={salva}
            disabled={salvando}
          >
            {salvando ? "Salvataggio…" : (inModifica ? "Salva modifiche" : "Crea operazione")}
          </button>
        </div>
      </div>
    </div>
  );
}
